/** The guest app's three kinds of step, from bookingmgmt: a participant's form
 *  (`ParticipantStep.js`), the pre-check-in step (`PreCheckinStep.js`, `ParticipantsList.js`,
 *  `WaiverList.js`, `WaiverText.js`) and the success screen (`SuccessStep.js`). The buttons
 *  move between the preview's steps; nothing is saved or sent. */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Checkbox, Chip, Divider, FormControlLabel, Typography } from '@mui/material';
import { useState } from 'react';

import { HtmlContent } from '@/components/RichText/HtmlContent';

import type { PreCheckField } from '../../model';
import { GuestButton } from '../guestTheme';
import { GUEST } from '../guestTokens';
import { fillVariables, participantName, waiverLanguage, type GuestPreviewOptions, type GuestPreviewWaiver } from '../previewModel';

import { GuestLanguageSelector } from './GuestChrome';
import { GuestFields, type FieldValues } from './GuestFields';

function StatusChip({ sx }: { sx?: object }) {
  return (
    <Chip
      label="Not pre-checked-in"
      size="small"
      sx={{ ml: 1, bgcolor: GUEST.incompleteBg, color: GUEST.incompleteText, ...sx }}
    />
  );
}

/** A participant step's buttons: in a row under the form from `lg`; on a phone, a bar
 *  fixed across the bottom of the screen (here: the frame's foot — `GuestPage`). */
export function ParticipantButtons({ lead, wide, onNext }: { lead: boolean; wide: boolean; onNext: () => void }) {
  const buttons = (
    <>
      {!lead && (
        <GuestButton variant="outlined" fullWidth={!wide} onClick={onNext}>
          Skip Member
        </GuestButton>
      )}
      <GuestButton variant="contained" fullWidth={!wide} startIcon={<SaveIcon />} onClick={onNext}>
        Save and Continue
      </GuestButton>
    </>
  );
  if (wide) return <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>{buttons}</Box>;
  return (
    <Box data-guest-bottom-bar sx={{ p: 1, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1 }}>
      {buttons}
    </Box>
  );
}

export interface ParticipantScreenProps {
  participant: number;
  fields: readonly PreCheckField[];
  values: FieldValues;
  onChange: (id: string, value: unknown) => void;
  touched: Readonly<Record<string, boolean>>;
  onBlur: (id: string) => void;
  options: GuestPreviewOptions;
  wide: boolean;
  onNext: () => void;
}

export function ParticipantScreen({ participant, fields, values, onChange, touched, onBlur, options, wide, onNext }: ParticipantScreenProps) {
  const lead = participant === 0;
  return (
    <Box sx={{ p: 1, bgcolor: 'background.paper', textAlign: 'left' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {lead ? 'Lead Booker' : 'Pre-check-in your party members'}
        </Typography>
        {lead && <StatusChip />}
      </Box>
      {!lead && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1, mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '16px' }}>
            Guest {participant}
          </Typography>
          <StatusChip sx={wide ? { ml: 0 } : { ml: 'auto' }} />
        </Box>
      )}
      <Box sx={{ mt: 3, pt: 0.5 }}>
        <GuestFields fields={fields} values={values} onChange={onChange} touched={touched} onBlur={onBlur} options={options} wide={wide} />
      </Box>
      {wide && <ParticipantButtons lead={lead} wide onNext={onNext} />}
    </Box>
  );
}

function WaiverBox({ waiver, lang, onLang, variables }: { waiver: GuestPreviewWaiver; lang: string | null; onLang: (l: string) => void; variables: Readonly<Record<string, string>> }) {
  const shown = waiverLanguage(waiver, lang);
  const html = shown ? (waiver.html[shown] ?? '') : '';
  return (
    <Box
      data-guest-waiver={waiver.id}
      data-guest-waiver-lang={shown ?? ''}
      sx={{ border: `1px solid ${GUEST.boxBorder}`, borderRadius: `${GUEST.radius}px`, bgcolor: 'white', p: 2, mt: 2, mb: 3 }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {waiver.name}
        </Typography>
        {waiver.languages.length > 1 && shown && (
          <GuestLanguageSelector languages={waiver.languages} value={shown} onChange={onLang} label={`${waiver.name} language`} />
        )}
      </Box>
      <Box sx={{ maxHeight: 300, overflowY: 'scroll', bgcolor: 'white', p: 2, pl: 0, mt: 1 }}>
        <HtmlContent
          html={fillVariables(html, variables, true)}
          sx={{ fontSize: '1rem', lineHeight: 1.5, textAlign: 'left', width: '100%', '& p': { my: 2, mx: 0 } }}
        />
      </Box>
    </Box>
  );
}

export interface CheckinScreenProps {
  names: readonly string[];
  waivers: readonly GuestPreviewWaiver[];
  lang: string | null;
  onLang: (l: string) => void;
  variables: Readonly<Record<string, string>>;
  wide: boolean;
  onDone: () => void;
  onCancel: () => void;
}

export function CheckinScreen({ names, waivers, lang, onLang, variables, wide, onDone, onCancel }: CheckinScreenProps) {
  const [consent, setConsent] = useState(false);
  return (
    <Box sx={{ p: 1, borderRadius: 2, textAlign: 'left' }}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }} gutterBottom>
          Pre-check-in for:
        </Typography>
        <Box data-guest-participants sx={{ display: 'flex', flexDirection: wide ? 'row' : 'column', flexWrap: wide ? 'wrap' : 'nowrap', gap: wide ? 0.5 : 0, alignItems: 'flex-start' }}>
          {names.map((name, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content', flexShrink: 0 }}>
              <Checkbox checked disabled size="small" inputProps={{ 'aria-label': name }} />
              <Typography variant="body1" sx={{ whiteSpace: 'nowrap' }}>
                {name}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      {waivers.map((w) => (
        <WaiverBox key={w.id} waiver={w} lang={lang} onLang={onLang} variables={variables} />
      ))}
      <Divider sx={{ mx: -3 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
        <FormControlLabel
          control={<Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} name="promoConsent" />}
          label={<Typography variant="body2">I allow my contact information to be used for promotional tours and offers</Typography>}
        />
        {/* The guest app asks for a `caption2` variant, which MUI does not have: the text
            inherits body1. */}
        <Typography variant="body1" display="block" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic', textAlign: 'left' }}>
          *By proceeding with pre-check-in, you confirm that you have permission to pre-check-in all members of your party and to receive tour-related
          communication.
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
        <GuestButton variant="contained" fullWidth onClick={onDone}>
          {waivers.length > 0 ? 'Sign Waiver(s) and Pre-check-in' : 'Pre-check-in'}
        </GuestButton>
        <GuestButton variant="outlined" fullWidth onClick={onCancel}>
          Cancel
        </GuestButton>
      </Box>
    </Box>
  );
}

export function DoneScreen({ lines, wide }: { lines: readonly string[]; wide: boolean }) {
  return (
    <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <CheckCircleIcon sx={{ fontSize: 32, color: 'success.light', mb: 2 }} />
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', fontSize: wide ? '1.5rem' : '1.25rem', lineHeight: 1.2, mx: -4, mb: 3 }}>
        You have successfully completed your pre-check-in!
      </Typography>
      <Typography variant="body1" color="text.primary" component="div" sx={{ mb: 2 }} data-guest-confirmation>
        {lines.map((line, i) => (
          <span key={i}>
            {line}
            <br />
          </span>
        ))}
      </Typography>
    </Box>
  );
}

/** The participants' names on the pre-check-in step. */
export function participantNames(values: readonly FieldValues[], count: number): string[] {
  return Array.from({ length: count }, (_, i) => participantName(values[i], i));
}
